import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, JobStatus } from "./StatusBadge";
import { Calendar, User, MoreVertical, AlertCircle, Users as UsersIcon, Repeat, Layers, MapPin, Phone, Mail, CalendarPlus, Receipt, FileCheck, Send, DollarSign, Clock, TrendingUp, FileText, DollarSign as DollarIcon, Flag, Zap, Eye, Edit3, Trash2, Copy } from "lucide-react";
import { useLocation } from "wouter";
import { formatDistanceToNow, format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface StaffMember {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
}

interface JobCardProps {
  id: string;
  jobNumber?: string;
  title: string;
  description?: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  status: JobStatus;
  date: string;
  assignedTo?: string; // Legacy: single staff member
  assignedStaff?: StaffMember[]; // Multi-staff support with full objects
  assignedStaffNames?: string[]; // Legacy: Multi-staff support with names only
  staffColor?: string;
  labels?: string[];
  isMultiDay?: boolean;
  isRecurring?: boolean;
  requiresTeam?: boolean;
  address?: string;
  invoiceStatus?: string | null;
  priority?: string | null;
  estimatedHours?: number | null;
  estimatedDuration?: number | null;
  quoteRequired?: boolean | null;
  quoteStatus?: string | null;
  quoteTotal?: string | null;
  invoiceTotal?: string | null;
  createdAt?: Date | string | null;
  actualStartDate?: Date | string | null;
  actualEndDate?: Date | string | null;
  onSelect?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
}

export function JobCard({
  id,
  jobNumber,
  title,
  description,
  customerName,
  customerPhone,
  customerEmail,
  status,
  date,
  assignedTo,
  assignedStaff,
  assignedStaffNames,
  staffColor,
  labels = [],
  isMultiDay = false,
  isRecurring = false,
  requiresTeam = false,
  address,
  invoiceStatus,
  priority,
  estimatedHours,
  estimatedDuration,
  quoteRequired,
  quoteStatus,
  quoteTotal,
  invoiceTotal,
  createdAt,
  actualStartDate,
  actualEndDate,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
}: JobCardProps) {
  const [, navigate] = useLocation();
  
  // Determine whether to show avatars or text
  const hasStaffObjects = assignedStaff && assignedStaff.length > 0;
  const displayStaffNames = assignedStaffNames && assignedStaffNames.length > 0
    ? assignedStaffNames
    : (assignedTo ? [assignedTo] : []);
    
  // Check if job has a scheduled date
  const isScheduled = date && date !== "Not scheduled";

  // Format duration
  const formatDuration = () => {
    if (estimatedHours) {
      if (estimatedHours < 1) return `${Math.round(estimatedHours * 60)} min`;
      if (estimatedHours === 1) return "1 hour";
      return `${estimatedHours.toFixed(1)} hours`;
    }
    if (estimatedDuration) {
      return `${estimatedDuration} min`;
    }
    return null;
  };

  // Get priority badge
  const getPriorityBadge = () => {
    if (!priority) return null;
    const priorityLower = priority.toLowerCase();
    if (priorityLower.includes('urgent') || priorityLower === 'high') {
      return { label: 'Urgent', variant: 'destructive' as const, icon: Zap };
    }
    if (priorityLower === 'medium') {
      return { label: 'Medium', variant: 'secondary' as const, icon: TrendingUp };
    }
    if (priorityLower === 'low') {
      return { label: 'Low', variant: 'outline' as const, icon: Flag };
    }
    return { label: priority, variant: 'outline' as const, icon: Flag };
  };

  const priorityInfo = getPriorityBadge();

  // Format amount
  const formatAmount = (amount: string | null | undefined) => {
    if (!amount) return null;
    const num = parseFloat(amount);
    if (isNaN(num)) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };
  
  return (
    <Card
      className="hover-elevate cursor-pointer"
      onClick={onSelect}
      data-testid={`card-job-${id}`}
      style={staffColor ? {
        borderLeft: `4px solid ${staffColor}`,
      } : undefined}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3 border-b">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {jobNumber && (
              <Badge variant="outline" className="text-xs font-mono max-w-[200px] overflow-hidden">
                <span className="truncate block">{jobNumber}</span>
              </Badge>
            )}
            {priorityInfo && (
              <Badge variant={priorityInfo.variant} className="text-xs flex items-center gap-1 max-w-[150px] min-w-0 overflow-hidden">
                <priorityInfo.icon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{priorityInfo.label}</span>
              </Badge>
            )}
            <div className="max-w-[200px] min-w-0 overflow-hidden">
              <StatusBadge status={status} />
            </div>
            {isRecurring && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 max-w-[150px] min-w-0 overflow-hidden">
                <Repeat className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">Recurring</span>
              </Badge>
            )}
            {isMultiDay && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 max-w-[150px] min-w-0 overflow-hidden">
                <Layers className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">Multi-day</span>
              </Badge>
            )}
            {requiresTeam && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 max-w-[150px] min-w-0 overflow-hidden">
                <UsersIcon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">Team</span>
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-base leading-tight mb-1.5 line-clamp-2" data-testid="text-job-title">
            {title}
          </h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="min-w-0 break-all" data-testid="text-job-customer">{customerName}</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
              }}
              data-testid="button-job-options"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (onSelect) {
                  onSelect();
                } else {
                  navigate(`/job/${id}`);
                }
              }}
              data-testid="menu-item-view-job"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Job
            </DropdownMenuItem>
            {onEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(id);
                }}
                data-testid="menu-item-edit-job"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Job
              </DropdownMenuItem>
            )}
            {onDuplicate && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(id);
                }}
                data-testid="menu-item-duplicate-job"
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Job
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(id);
                  }}
                  className="text-destructive focus:text-destructive"
                  data-testid="menu-item-delete-job"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Job
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {/* Address - Always show if available */}
        {address && (
          <div className="flex items-start gap-2 text-sm  ">
            <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <span className="text-muted-foreground flex-1 line-clamp-2" data-testid="text-job-address">
              {address}
            </span>
          </div>
        )}

        {/* Description - Always show if available */}
        {description && (
          <div className="text-sm text-muted-foreground line-clamp-3  " data-testid="text-job-description">
            {description}
          </div>
        )}

        {/* Quote/Invoice Financial Info */}
        {(quoteTotal || invoiceTotal) && (
          <div className="flex items-center justify-between py-2 ">
            {quoteTotal && quoteStatus && (
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Quote</p>
                  <p className="text-sm font-semibold">{formatAmount(quoteTotal)}</p>
                </div>
              </div>
            )}
            {invoiceTotal && (
              <div className="flex items-center gap-2 ml-auto">
                <DollarIcon className="h-4 w-4 text-muted-foreground" />
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Invoice</p>
                  <p className="text-sm font-semibold">{formatAmount(invoiceTotal)}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Key Details - Always show schedule and quote status */}
        <div className="space-y-2.5 py-2 border-b">


          {/* Duration/Time Estimate - Show if available */}
          {formatDuration() && (
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground mb-0.5">Est. Duration</p>
                <p className="text-sm font-medium">{formatDuration()}</p>
              </div>
            </div>
          )}
        </div>

        {/* Contact Info - Show if available */}
        {(customerPhone || customerEmail) && (
          <div className="flex items-center gap-3 flex-wrap text-xs py-2 border-b">
            {customerPhone && (
              <a
                href={`tel:${customerPhone}`}
                className="flex items-start gap-2 text-muted-foreground hover:text-foreground hover:underline transition-colors min-w-0 max-w-full sm:max-w-[calc(50%-0.375rem)]"
                onClick={(e) => e.stopPropagation()}
                data-testid="link-customer-phone"
              >
                <Phone className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="min-w-0 break-all">{customerPhone}</span>
              </a>
            )}
            {customerEmail && (
              <a
                href={`mailto:${customerEmail}`}
                className="flex items-start gap-2 text-muted-foreground hover:text-foreground hover:underline transition-colors min-w-0 max-w-full sm:max-w-[calc(50%-0.375rem)]"
                onClick={(e) => e.stopPropagation()}
                data-testid="link-customer-email"
              >
                <Mail className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="min-w-0 break-all">{customerEmail}</span>
              </a>
            )}
          </div>
        )}

        {/* Show message when minimal data */}
        {!address && !description && !(customerPhone || customerEmail) && !formatDuration() && (
          <div className="text-xs text-muted-foreground italic py-2 border-b">
            Click to view details and add more information
          </div>
        )}

        {/* Assigned Staff - Always show */}
        <div className="flex items-start gap-2 py-2 border-b">
          <User className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-1">Assigned Staff</p>
            { displayStaffNames.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 min-w-0 max-w-full overflow-hidden">
                {displayStaffNames.map((name, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs max-w-full min-w-0 whitespace-normal break-all" data-testid={`badge-staff-${idx}`}>
                    <span className="block break-all">{name}</span>
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="block max-w-full whitespace-normal break-all text-xs text-muted-foreground italic">Unassigned</span>
            )}
          </div>
        </div>

        {/* Labels/Tags */}
        {labels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 py-2">
            {labels.map((label, idx) => {
              const isUrgent = label.toLowerCase().includes("urgent");
              return (
                <Badge
                  key={idx}
                  variant={isUrgent ? "destructive" : "outline"}
                  className="text-xs max-w-[200px] min-w-0 overflow-hidden"
                >
                  {isUrgent && <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />}
                  <span className="truncate">{label}</span>
                </Badge>
              );
            })}
          </div>
        )}

        {/* Created Date */}
        {createdAt && (
          <div className="text-xs text-muted-foreground pt-1">
            Created {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
          </div>
        )}
        
        {/* Invoice Status Badge - Only show for completed jobs with invoice status */}
        {(status === 'complete' || status === 'invoiced' || status === 'paid') && invoiceStatus && (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Badge
              variant={
                invoiceStatus === 'paid' ? 'default' :
                invoiceStatus === 'sent' ? 'secondary' :
                'outline'
              }
              className="text-xs flex items-center gap-1 max-w-[200px] min-w-0 overflow-hidden"
              data-testid="badge-job-invoice-status"
            >
              {invoiceStatus === 'pending' && (
                <>
                  <Receipt className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">Pending Invoice</span>
                </>
              )}
              {invoiceStatus === 'created' && (
                <>
                  <FileCheck className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">Invoice Created</span>
                </>
              )}
              {invoiceStatus === 'sent' && (
                <>
                  <Send className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">Invoice Sent</span>
                </>
              )}
              {invoiceStatus === 'paid' && (
                <>
                  <DollarSign className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">Paid</span>
                </>
              )}
            </Badge>
          </div>
        )}
        
        {status === 'quote-needed' && (
          <div className="pt-2 border-t mt-2">
            <Button
              variant="default"
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/quote/new?jobId=${id}`);
              }}
              data-testid="button-create-quote"
            >
              <FileText className="h-4 w-4 mr-2" />
              Create quote
            </Button>
          </div>
        )}

        {!isScheduled && status !== 'quote-needed' && (
          <div className="pt-2 border-t mt-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/job/${id}`);
                setTimeout(() => {
                  const schedulingSection = document.getElementById('scheduling-section');
                  if (schedulingSection) {
                    schedulingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }}
              data-testid="button-schedule-job"
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Schedule
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
